/* global HsNavScroller */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Table from "./Table";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import UpsertList from "../../modals/UpsertList";
import axiosInstance from "../../utils/Requests/axios";
import ProductFilterModal from "../../modals/ProductFilterModal";
import ModalFilterSave from "../../modals/FilterSaveModal";
import TablePagination from "../Common/Pagination";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import Footer from "../Footer";
import { useLoading } from "../../utils/Contexts/LoadingContext";
import FilterIcon from "../svg/FilterIcon";

function BacklinksOffers() {
  const { t } = useContext(LanguageContext);
  const { isLoading, setIsLoading } = useLoading();
  const { userData } = useContext(UserContext);
  const [listsData, setListsData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [listEditMode, setListEditMode] = useState(false);
  const [columns, setColumns] = useState({
    name: true,
    dr: true,
    tat: true,
    traffic: true,
    niches: true,
    language: true,
    guest_post: true,
    link_insert: true,
    links: true,
    domain_age: true,
  });
  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
    6: null,
    7: null,
    8: null,
    9: null,
    10: null,
  });

  const listsDataCalled = useRef(false);
  const getListsData = () => {
    if (listsDataCalled.current) return;
    listsDataCalled.current = true;
    const url = `/users/${userData?.uuid}/preferences/lists`;
    setIsLoading(true);
    axiosInstance
      .get(url)
      .then((response) => {
        setListsData(response?.data);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the lists data"));
      });
  };

  const filterDataCalled = useRef(false);
  const loadFilters = () => {
    if (filterDataCalled.current) return;
    filterDataCalled.current = true;
    const url = `/users/${userData?.uuid}/preferences/filters`;
    setIsLoading(true);
    axiosInstance
      .get(url)
      .then((response) => {
        setFilterData(response?.data);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the filters data"));
      });
  };

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const handleDeleteList = () => {
    const url = `/users/${userData?.uuid}/preferences/lists/${selectedList?.uuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        setActiveTab("All");
        const filteredListsData = listsData.filter(
          (listItem) => listItem.uuid !== selectedList?.uuid
        );
        setListsData(filteredListsData);
        setSelectedList(null);
        toast.success(t("List deleted successfully"));
      })
      .catch(() => {
        toast.error(t("There was an error deleting the list"));
      });
  };

  const handleDeleteFilter = () => {
    const url = `/users/${userData?.uuid}/preferences/filters/${selectedFilter.uuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        setActiveTab("All");
        const filteredFilterData = filterData.filter(
          (filterItem) => filterItem.uuid !== selectedFilter.uuid
        );
        setSelectedFilter(null);
        setFilterData(filteredFilterData);
        toast.success(t("Filter deleted successfully"));
      })
      .catch(() => {
        toast.error(t("There was an error deleting the filter"));
      });
  };

  const handleFilterClick = (filter) => () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      search: {
        filters: filter?.filters,
      },
    }));
    setSelectedFilter(filter);
    setActiveTab("Filtered");
  };

  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "uuid" },
      { data: "domain" },
      { data: "dr" },
      { data: "tat" },
      { data: "traffic" },
      { data: "niches" },
      { data: "language" },
      { data: "placements.type.1" },
      { data: "placements.type.2" },
      { data: "rels" },
    ],
    order: [],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  useEffect(() => {
    const payload = { ...formData };
    const order = [];
    Object.keys(columnSorts).forEach((columnIndex) => {
      if (columnSorts[columnIndex]) {
        order.push({
          column: columnIndex,
          dir: columnSorts[columnIndex],
        });
      }
    });
    if (order.length) {
      payload.order = order;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchDomainsData = () => {
    const payload = _.cloneDeep(formData);

    if (selectedList) {
      payload.search.filters.push({
        key: "list",
        value: selectedList.uuid,
      });
    }

    const url = `/offers-search`;
    setIsLoading(true);
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchDomainsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const previousTabRef = useRef(activeTab);
  useEffect(() => {
    if (!_.isEqual(previousTabRef.current, activeTab)) {
      previousTabRef.current = activeTab;
      fetchDomainsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (userData?.uuid) {
      getListsData();
      loadFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uuid]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (!hsInitRef.current) {
      new HsNavScroller(".js-nav-scroller");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hsInitRef.current = true;
    }
  }, []);

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="align-items-center mb-3">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb breadcrumb-no-gutter">
                  <li className="breadcrumb-item">
                    <a className="breadcrumb-link" href="/offers-search">
                      {t("Backlinks")}
                    </a>
                  </li>
                  {activeTab !== "All" && (
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("Backlinks Offers Search")}
                    </li>
                  )}
                </ol>
              </nav>

              <div className="col-md mb-2 mb-md-0">
                <h1 className="page-header-title">
                  {activeTab === "All"
                    ? t("Backlinks Offers Search")
                    : activeTab === "Filtered" ||
                      activeTab === "Blacklist" ||
                      activeTab === "Favorites"
                    ? activeTab
                    : selectedList.name}
                </h1>
                {!(
                  activeTab === "All" ||
                  activeTab === "Filtered" ||
                  activeTab === "Blacklist" ||
                  activeTab === "Favorites"
                ) && (
                  <div className="mt-2">
                    {/* eslint-disable-next-line */}
                    <a
                      className="text-body me-3 cursor-pointer"
                      tabIndex="-1"
                      aria-disabled="false"
                      data-bs-toggle="modal"
                      data-bs-target="#modal-sm-create-list"
                      onClick={() => setListEditMode(true)}
                    >
                      <i className="bi-pencil-square me-1"></i> {t("Modify")}
                    </a>
                    {/* eslint-disable-next-line */}
                    <a
                      className="text-danger cursor-pointer"
                      onClick={() => handleDeleteList()}
                    >
                      <i className="bi-x-circle me-1"></i> {t("Delete")}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="js-nav-scroller hs-nav-scroller-horizontal">
            <span
              className="hs-nav-scroller-arrow-prev"
              style={{ display: "none" }}
            >
              {/* eslint-disable-next-line */}
              <a className="hs-nav-scroller-arrow-link">
                <i className="bi-chevron-left"></i>
              </a>
            </span>

            <span
              className="hs-nav-scroller-arrow-next"
              style={{ display: "none" }}
            >
              {/* eslint-disable-next-line */}
              <a className="hs-nav-scroller-arrow-link">
                <i className="bi-chevron-right"></i>
              </a>
            </span>

            <ul className="nav nav-tabs page-header-tabs">
              <li className="nav-item">
                <button
                  onClick={() => {
                    setSelectedList(null);
                    setActiveTab("All");
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      search: {
                        filters: [],
                      },
                    }));
                  }}
                  className={`${
                    activeTab === "All" ? "nav-link active" : "nav-link"
                  }`}
                  disabled={isLoading}
                >
                  {t("All")}
                </button>
              </li>
              <li className="nav-item">
                {/* eslint-disable-next-line */}
                <button
                  onClick={() => {
                    setSelectedList(null);
                    setActiveTab("Filtered");
                  }}
                  className={`${
                    activeTab === "Filtered" ? "nav-link active" : "nav-link"
                  }`}
                  disabled={isLoading}
                >
                  {t("Filtered")}
                </button>
              </li>
              {listsData.map((list) => (
                <li key={list.uuid} className="nav-item">
                  {/* eslint-disable-next-line */}
                  <button
                    className={`${
                      activeTab === list?.name ? "nav-link active" : "nav-link"
                    }`}
                    tabIndex="-1"
                    onClick={() => {
                      setSelectedList(list);
                      setActiveTab(list.name);
                    }}
                    disabled={isLoading}
                  >
                    {list?.name}
                  </button>
                </li>
              ))}
              <li className="nav-item">
                <button
                  className="nav-link"
                  tabIndex="-1"
                  aria-disabled="false"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-sm-create-list"
                >
                  <i className="bi-plus"></i> {t("Add List")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="card">
          <div className="card-header card-header-content-between">
            <div className="mb-sm-0">
              <div className="input-group input-group-merge input-group-flush border rounded-2 pe-2">
                <div className="input-group-prepend input-group-text px-2">
                  <i className="bi-search"></i>
                </div>
                <input
                  id="datatableSearch"
                  type="search"
                  className="form-control custom-placeholder"
                  placeholder={t("Search Domains")}
                  aria-label="Search Domains"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="d-grid d-flex justify-content-end align-items-center gap-2">
              <div className="btn-group dropupend">
                <button
                  className="btn btn-white text-nowrap filter-btn"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasEcommerceProductFilter"
                  aria-controls="offcanvasEcommerceProductFilter"
                >
                  <FilterIcon /> {t("Filters")}{" "}
                  {formData?.search?.filters.length
                    ? `(${formData?.search?.filters.length})`
                    : ""}
                </button>
                <button
                  type="button"
                  className="btn btn-white dropdown-toggle dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="visually-hidden">{t("Load Filters")}</span>
                </button>
                <div
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButtonHoverAnimation"
                >
                  {filterData?.length ? (
                    <>
                      <span className="dropdown-header">
                        {t("Load Filters")}
                      </span>
                      {filterData?.map((filter, index) => (
                        // eslint-disable-next-line
                        <a
                          className="dropdown-item"
                          key={index}
                          onClick={handleFilterClick(filter)}
                        >
                          <i
                            className={`${
                              filter?.notify ? "bi bi-bell" : "bi bi-bell-slash"
                            } me-1`}
                          />
                          {filter?.name}
                        </a>
                      ))}
                      <div className="dropdown-divider"></div>
                    </>
                  ) : (
                    ""
                  )}
                  <span className="dropdown-header">
                    {t("Current Filters")}
                  </span>
                  {/* eslint-disable-next-line */}
                  <a
                    className={`dropdown-item${
                      selectedFilter || formData?.search?.filters.length
                        ? ""
                        : " disabled"
                    }`}
                    data-bs-toggle="modal"
                    data-bs-target="#modal-sm-filters-save"
                  >
                    <i className="bi-plus-circle-dotted dropdown-item-icon me-1"></i>
                    {selectedFilter ? "Modify" : "Save"}
                  </a>
                  {/* eslint-disable-next-line */}
                  <a
                    className={`dropdown-item${
                      selectedFilter ? " text-danger" : " disabled"
                    }`}
                    onClick={() => handleDeleteFilter()}
                  >
                    <i className="bi-x-circle dropdown-item-icon me-1"></i>
                    {t("Delete")}
                  </a>
                </div>
              </div>

              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap filter-btn"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Table
            columnsVisibility={columns}
            data={tableData}
            listsData={listsData}
            fetchDomainsData={fetchDomainsData}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
            selectedList={selectedList}
            setFormData={setFormData}
            activeTab={activeTab}
            setData={setTableData}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>

      <Footer />
      <UpsertList
        selectedList={selectedList}
        listEditMode={listEditMode}
        setListEditMode={setListEditMode}
        setListsData={setListsData}
        listsData={listsData}
      />
      <ProductFilterModal
        domainData={tableData}
        listsData={listsData}
        formData={formData}
        setFormData={setFormData}
        applyFilter={() =>
          activeTab === "All" ? setActiveTab("Filtered") : null
        }
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <ModalFilterSave
        formData={formData}
        filterData={filterData}
        setFilterData={setFilterData}
        selectedFilter={selectedFilter}
      />
    </main>
  );
}

export default BacklinksOffers;
